import styled from '@emotion/styled';
import React, { ComponentProps, HTMLAttributes } from 'react';
import { ContentConditionalFlag, ContentFlag } from './apiService';
import { __t } from './i18n';
import { IconAudio, IconBrand, IconCamQuality, IconCinemaBadge, MuteIcon, TheaterIcon } from './icons';
import { Icon, Tag } from './styles';

const CardBadge = ({ flag, customPosition, customColor, ...containerProps }: CardBadgeProps) => {
  const arr = flag.split(', ').filter((f) => !!flagProps[f]) as ContentFlag[];
  if (!arr.length) return null;
  const topFlags = filterByPosition(arr, 'top').slice(-1);
  const bottomFlags = filterByPosition(arr, 'bottom').slice(0, 2);

  const renderBadge = (items: ContentFlag[], type: 'top' | 'bottom') => items.map((f, index) => {
    const { position, color, icon } = flagProps[f] || {};

    const finalPosition = customPosition ?? position;
    const finalColor = customColor ?? color;

    const hasCustomPosition =
            customPosition ?? (type === 'bottom' && index === 1);

    return (
      <Container
        key={f}
        position={finalPosition}
        index={index}
        hasCustomPosition={!!hasCustomPosition}
        {...containerProps}
      >
        <CornerTag
          compact
          size="small"
          px="0.5rem"
          position={finalPosition}
          color={finalColor}
        >
          {icon && <Icon ml=".5em">{icon}</Icon>}
          {__t('flag.' + f)}
        </CornerTag>
      </Container>
    );
  });

  return (
    <>
      {renderBadge(topFlags, 'top')}
      {renderBadge(bottomFlags, 'bottom')}
    </>
  );
};

// ----- //
// UTILS //
// ----- //

const flagProps: Partial<Record<Flag, FlagProps>> = {
  coming_soon: {
    position: 'bottom',
    color: 'warning',
  },
  traffic_free: {
    position: 'bottom',
    color: 'success',
  },
  free_content: {
    position: 'bottom',
    color: 'sweet',
  },
  silent_film: {
    position: 'bottom',
    color: 'default',
    icon: <MuteIcon />,
  },
  theater_film: {
    position: 'top',
    color: 'primary',
    icon: <TheaterIcon />,
  },
  cam_quality: {
    position: 'top',
    color: 'primary',
    icon: <IconCamQuality />,
  },
  in_production: {
    position: 'top',
    color: 'primary',
    icon: <IconBrand />,
  },
  exclusive_dub: {
    position: 'top',
    color: 'primary',
    icon: <IconAudio />,
  },
  cinema_online: {
    position: 'top',
    color: 'primary',
    icon: <IconCinemaBadge />,
  },
  audio_book: {
    position: 'top',
    color: 'primary',
  },
  persian_dub: {
    position: 'top',
    color: 'primary',
    icon: <IconAudio />,
  },
};

const filterByPosition = (flags: ContentFlag[], p: Position) => {
  return flags.filter((f) => flagProps[f]?.position === p);
};

// ------ //
// STYLES //
// ------ //

const Container = styled.div<TagProps>(({ position, index, hasCustomPosition }) => {
  const offsetTop: string | number = hasCustomPosition ? (Number(index) * 32) + (-1) : -1;
  const offsetBottom: string | number = hasCustomPosition ? (Number(index) * 32) + (-1) : -1;
  const offset: string | number = -1;
  const positions = position === 'top' ? { top: offsetTop, right: offset } : { bottom: offsetBottom, left: offset };

  return {
    position: 'absolute',
    zIndex: 1,
    pointerEvents: 'none',
    ...positions,
  };
});

const getRadius = (r: string, p?: Position) => {
  if (p === 'top') return `${r} 0 0 ${r}`;
  if (p === 'bottom') return `0 ${r} ${r} 0`;

  return r;
};

const CornerTag = styled(Tag)<TagProps>(({ theme, position }) => {
  const borderRadius = getRadius(theme.radius.pill, position);
  return { borderRadius };
});

// ----- //
// TYPES //
// ----- //

type Flag = ContentFlag | ContentConditionalFlag;
type Position = 'top' | 'bottom';

type CardBadgeProps = {
  flag: string,
  customPosition?: Position | undefined,
  customColor?: ComponentProps<typeof Tag>['color']
} & HTMLAttributes<HTMLDivElement>;

interface TagProps {
  position?: Position,
  index?: number,
  hasCustomPosition?: boolean
}

interface FlagProps extends TagProps {
  position: Position;
  color: ComponentProps<typeof Tag>['color'];
  // eslint-disable-next-line no-undef
  icon?: JSX.Element;
}

export default CardBadge;
